// Email attribution persistence.
//
// Cold-outreach links carry a `?emailaddress=` param (the same param name the
// separate marketing-lead-scripts backend already expects) so an anonymous
// visitor's on-site engagement can later be matched back to their email, even
// if they never submit the office-cleaning enquiry form. Stored in
// localStorage so it survives client-side navigation and return visits within
// the TTL window, mirroring utmAttribution.ts.
//
// TTL: 30 days, same as UTM attribution.

const EMAIL_KEY = "hh:email_attribution";
const MAX_EMAIL_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface EmailAttribution {
  emailaddress: string;
  captured_at: number;
}

/**
 * Call on every page load that has an `emailaddress` param in the URL.
 * Only writes when the param is present.
 * Does NOT overwrite a still-valid existing attribution — preserves first-touch.
 */
export function saveEmailAttribution(searchParams: URLSearchParams): void {
  const emailaddress = searchParams.get("emailaddress");
  if (!emailaddress) return;

  persistEmailAttribution(emailaddress);
}

/**
 * Call when a visitor types a valid email into the office-cleaning enquiry
 * form (see analytics/captureLeadEmail.ts). Same first-touch rule as the link
 * param: an existing, still-valid attribution is kept.
 */
export function saveTypedEmailAttribution(emailaddress: string): void {
  if (!emailaddress) return;

  persistEmailAttribution(emailaddress);
}

function persistEmailAttribution(emailaddress: string): void {
  // Preserve first-touch: don't overwrite if a valid attribution already exists
  if (getEmailAttribution()) return;

  const attribution: EmailAttribution = {
    emailaddress,
    captured_at: Date.now(),
  };

  try {
    localStorage.setItem(EMAIL_KEY, JSON.stringify(attribution));
  } catch {
    // localStorage unavailable — fail silently
  }
}

/**
 * Returns the stored attribution if it exists and has not expired.
 * Prunes stale entries automatically.
 */
export function getEmailAttribution(): EmailAttribution | null {
  try {
    const raw = localStorage.getItem(EMAIL_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as EmailAttribution;

    if (
      !Number.isFinite(parsed.captured_at) ||
      Date.now() - parsed.captured_at > MAX_EMAIL_TTL_MS
    ) {
      localStorage.removeItem(EMAIL_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/**
 * Call after a successful office-cleaning enquiry submission so a converted
 * visitor's later, unrelated sessions stop being tagged with this email.
 */
export function clearEmailAttribution(): void {
  try {
    localStorage.removeItem(EMAIL_KEY);
  } catch {
    // Attribution cleanup is best-effort.
  }
}
